<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary"> mdi-cog </v-icon>
        <span class="px-2">{{$t('settings')}}</span>
      </v-card-title>
      <v-card-text>
        <vue-element-loading :active="isLoading" />
        <v-alert v-if="isDemo" type="info" dense text class="mb-4">
          {{ $t('demo_settings_disabled') }}
        </v-alert>
        <v-form ref="form" v-if="settings" v-model="valid" lazy-validation class="my-2">
          <v-row>
            <v-col cols="12" md="3" class="my-2">
              <v-autocomplete
                v-model="selectedCurrency"
                :items="currencies"
                dense
                outlined
                item-text="name"
                item-value="id"
                :label="$t('currency')"
                :filter="customFilter"
                persistent-hint
                return-object
                required
                :rules="requiredRules"
                :hint="
                  selectedCurrency != null
                    ? selectedCurrency.code + ' (' + selectedCurrency.symbol + ')'
                    : ''
                "
                @change="currencySelected"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.base_price"
                  type="number"
                  outlined
                  dense
                  :label="$t('base_price')"
                  :placeholder="$t('enter_base_price')"
                  persistent-hint
                  required
                  :rules="numberRulesZeroAllowed"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.rate_per_km"
                  type="number"
                  outlined
                  dense
                  :label="$t('rate_per_km')"
                  :placeholder="$t('enter_rate_per_km')"
                  persistent-hint
                  required
                  :rules="numberRulesZeroAllowed"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.commission"
                  type="number"
                  outlined
                  dense
                  :label="$t('commission_percent')"
                  :placeholder="$t('enter_commission_percentage')"
                  persistent-hint
                  required
                  :rules="percentRules"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.max_reservations_per_trip"
                  type="number"
                  outlined
                  dense
                  :label="$t('max_reservations_per_trip')"
                  :placeholder="$t('enter_max_reservations')"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.publish_trips_future_days"
                  type="number"
                  outlined
                  dense
                  :label="$t('publish_trips_future_days')"
                  :placeholder="$t('enter_days_future')"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <!-- max_distance_to_stop -->
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.max_distance_to_stop"
                  type="number"
                  outlined
                  dense
                  :label="$t('max_distance_to_stop')"
                  :placeholder="$t('enter_max_distance')"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
            <!-- distance_to_stop_to_mark_arrived -->
            <v-col cols="12" md="3" class="my-2">
                <v-text-field
                  v-model="settings.distance_to_stop_to_mark_arrived"
                  type="number"
                  outlined
                  dense
                  :label="$t('distance_to_mark_arrived')"
                  :placeholder="$t('enter_distance_arrived')"
                  persistent-hint
                  required
                  :rules="numberRules"
                ></v-text-field>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{$t('bus_settings')}}</label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.allow_seat_selection"
                :label="$t('allow_seat_selection')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{$t('security_settings')}}</label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.otp_required"
                :label="$t('allow_otp')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{$t('ads_settings')}}</label>
          </div>
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="settings.allow_ads_in_driver_app"
                :label="$t('allow_ads_driver_app')"
                persistent-hint
              ></v-checkbox>
              <v-checkbox
                v-model="settings.allow_ads_in_customer_app"
                :label="$t('allow_ads_customer_app')"
                persistent-hint
              ></v-checkbox>
            </v-col>
          </v-row>
          <div class="mt-8">
            <label class="text--secondary font-weight-bold">{{ $t('map_settings') }}
                <info-tool-tip class="ml-2"
            :message="$t('map_settings_tooltip')"></info-tool-tip>
            </label>
          </div>
          <v-row class="my-2">
            <v-col cols="12" md="4">
              <!-- selected automatically from the filled keys, see updateMapProvider -->
              <v-radio-group v-model="settings.map_provider" row disabled class="mt-0">
                <v-radio :label="$t('map_provider_google')" value="google"></v-radio>
                <v-radio :label="$t('map_provider_mapbox')" value="mapbox"></v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field
                    v-model="settings.google_maps_api_key"
                    outlined
                    dense
                    :label="$t('google_maps_api_key')"
                    :placeholder="$t('enter_google_maps_api_key')"
                    persistent-hint
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field
                    v-model="settings.mapbox_access_token"
                    outlined
                    dense
                    :label="$t('mapbox_access_token')"
                    :placeholder="$t('enter_mapbox_access_token')"
                    persistent-hint
                ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn :disabled="!valid" color="primary" @click="saveSettings">
            {{$t('save')}}
            <v-icon right dark> mdi-content-save </v-icon>
          </v-btn>
        </v-card-actions>
    </v-card>
  </div>
</template>

<script>

import InfoToolTip from "@/components/InfoToolTip";
import VueElementLoading from "vue-element-loading";
import MapConfigService from '@/services/MapConfigService'
import { isDemoMode } from '@/config'

export default {
  components: {
    VueElementLoading,
    InfoToolTip,
  },
  data() {
    return {
      currencies: [],
      isLoading: false,
      settings: null,
      valid: true,
      requiredRules: [(v) => !!v || this.$t("required")],
      numberRules: [(v) => /^(0*[1-9][0-9]*(\.[0-9]+)?|0+\.[0-9]*[1-9][0-9]*)$/.test(v) || this.$t("must_be_greater_than_0")],
      numberRulesZeroAllowed: [(v) => /^(0*[0-9]+(\.[0-9]+)?|0+\.[0-9]*[1-9][0-9]*)$/.test(v) || this.$t("must_be_0_or_greater")],
      percentRules: [(v) => /^100$|^[0-9]{1,2}$|^[0-9]{1,2}\,[0-9]{1,3}$/.test(v) || this.$t("must_be_0_to_100")],
      selectedCurrency: null,
    };
  },
  computed: {
    isDemo() {
      return isDemoMode();
    },
  },
  mounted() {
    this.loadSettings();
  },
  watch: {
    "settings.google_maps_api_key": function () {
      this.updateMapProvider();
    },
    "settings.mapbox_access_token": function () {
      this.updateMapProvider();
    },
  },
  methods: {
    // the provider follows the filled keys, google wins when both are filled
    // (the server applies the same rule when saving)
    updateMapProvider() {
      if (!this.settings) return;
      const hasGoogleKey = !!(this.settings.google_maps_api_key || "").trim();
      const hasMapboxToken = !!(this.settings.mapbox_access_token || "").trim();
      this.settings.map_provider = !hasGoogleKey && hasMapboxToken ? "mapbox" : "google";
    },
    customFilter (item, queryText, itemText) {
      const textOne = item.name.toLowerCase()
      const textTwo = item.code.toLowerCase()
      const searchText = queryText.toLowerCase()

      return textOne.indexOf(searchText) > -1 ||
        textTwo.indexOf(searchText) > -1
    },
    //API Calls
    saveSettings() {
      if (this.isDemo) {
        this.$swal(this.$t("demo_mode"), this.$t("demo_settings_disabled"), "info");
        return;
      }
      this.submiting = true;
      axios
        .post("/settings/update", this.settings)
        .then((response) => {
          this.submiting = false;
          MapConfigService.invalidate();
          this.$notify({
            title: "Success",
            text: this.$t("settings_updated"),
            type: "success",
          });
        })
        .catch((error) => {
          this.submiting = false;
          this.$notify({
            title: "Error",
            text: (error.response && error.response.data && (error.response.data.message ||
                (Array.isArray(error.response.data.errors) && error.response.data.errors[0]))) || this.$t("error_updating_settings"),
            type: "error",
          });
          console.log(error);
        });
    },
    loadSettings() {
      this.isLoading = true;
      this.settings = [];
      axios
        .get(`/settings/all`)
        .then((response) => {
          this.settings = response.data;
          this.loadCurrencies();
          this.selectedCurrency = this.settings.currency;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: this.$t("error_retrieving_settings"),
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    loadCurrencies() {
      this.isLoading = true;
      this.currencies = [];
      axios
        .get(`/currencies/all`)
        .then((response) => {
          this.currencies = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: this.$t("error_retrieving_currencies"),
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response.data.message, "error");
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    validate() {
      this.valid = false;
      let v = this.$refs.form.validate();
      if (v) {
        this.valid = true;
        return true;
      }
      return false;
    },
    currencySelected(r) {
      this.selectedCurrency = r;
      this.settings.currency_id = this.selectedCurrency.id;
    },
  },
};
</script>
